/**
 * ImportInventoryByTypeModal
 * Lets coordinators bulk-import stock level records (part # + qty on hand)
 * for a selected location type (Warehouse / FSL / On-Site)
 .* Writes into the module-level `stockLevels` array in src/data/inventory.js
 .*/
import { useState, useRef, useCallback, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import dayjs from 'dayjs';

import { stockLevels, parts, warehouses } from '../../services/dataClient';

// ─── Constants ────────────────────────────────────────────────────────────────
const LOCATION_TYPES = ['Warehouse', 'FSL', 'On-Site'];

const TH = {
  backgroundColor: '#eef2f6',
  fontWeight: 600,
  fontSize: '0.75rem',
  color: 'var(--grey-600)',
  textTransform: 'none',
  letterSpacing: 'normal',
  px: 2,
  py: '10px',
  borderBottom: '1px solid var(--grey-200)',
  whiteSpace: 'nowrap',
};

const TD = {
  px: 2,
  py: 0,
  border: 'none',
  fontSize: '0.8125rem',
  color: 'var(--grey-800)',
  height: 44,
};

// Fake new rows per type — these part+location combos do NOT exist in stockLevels
const MOCK_NEW = {
  Warehouse: [
    { partNumber: 'SSD-3840-NVMe', locationName: 'Central Warehouse — Dallas',  quantityOnHand: 6  },
    { partNumber: 'HBA-16G-FC',    locationName: 'East Coast Warehouse',         quantityOnHand: 4  },
  ],
  FSL: [
    { partNumber: 'RAM-32GB-DDR5', locationName: 'FSL Los Angeles',              quantityOnHand: 8  },
    { partNumber: 'PWR-1100-DC',   locationName: 'FSL Chicago',                  quantityOnHand: 5  },
  ],
  'On-Site': [
    { partNumber: 'NIC-10G-RJ45',  locationName: 'On-Site — Apex Logistics Corp',       quantityOnHand: 3 },
    { partNumber: 'FAN-CHASSIS-4U', locationName: 'On-Site — Lakeside Financial Group',  quantityOnHand: 2 },
  ],
};

// ─── Status chip ──────────────────────────────────────────────────────────────
function StatusChip({ status }) {
  const cfg = {
    New:     { bg: '#E0F2F1', color: '#00695C' },
    Update:  { bg: '#E3F2FD', color: '#1565C0' },
    Invalid: { bg: '#FFEBEE', color: '#B71C1C' },
  }[status] ?? { bg: '#F5F5F5', color: '#757575' };

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', px: '8px', py: '2px', borderRadius: '10px', backgroundColor: cfg.bg }}>
      <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: cfg.color, lineHeight: 1 }}>
        {status}
      </Typography>
    </Box>
  );
}

// ─── Main modal ───────────────────────────────────────────────────────────────
export default function ImportInventoryByTypeModal({ open, defaultType = 'Warehouse', onClose, onImported }) {
  const [step,        setStep]        = useState(1);
  const [locType,     setLocType]     = useState(defaultType);
  const [fileName,    setFileName]    = useState('');
  const [previewRows, setPreviewRows] = useState([]);
  const [dragOver,    setDragOver]    = useState(false);
  const fileInputRef = useRef(null);

  // Reset + sync default type whenever modal opens
  useEffect(() => {
    if (open) {
      setStep(1);
      setFileName('');
      setPreviewRows([]);
      setDragOver(false);
      setLocType(defaultType);
    }
  }, [open, defaultType]);

  // Build deterministic mock preview for the given type
  const buildPreview = useCallback((name, type) => {
    const today = dayjs().format('YYYY-MM-DD');

    // 2 existing stockLevel records of this type → "Update"
    const updateRows = stockLevels
      .filter((sl) => sl.locationType === type)
      .slice(0, 2)
      .map((sl) => ({
        partNumber:      sl.partNumber,
        locationName:    sl.locationName,
        quantityOnHand:  sl.quantityOnHand + 2,   // simulate refreshed qty from file
        status:          'Update',
        _existingId:     sl.id,
        _today:          today,
      }));

    // Fake new combos → "New" (validate partNumber against parts list)
    const newRows = (MOCK_NEW[type] ?? []).map((r) => {
      const partValid = parts.some((p) => p.partNumber === r.partNumber);
      return { ...r, status: partValid ? 'New' : 'Invalid', _today: today };
    });

    setFileName(name);
    setPreviewRows([...updateRows, ...newRows]);
    setStep(2);
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) buildPreview(file.name, locType);
    e.target.value = '';
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) buildPreview(file.name, locType);
  }, [buildPreview, locType]);

  const handleImport = () => {
    const today = dayjs().format('YYYY-MM-DD');

    // Resolve geo data from warehouses for a given locationName
    const geoFor = (locationName) => {
      const wh = warehouses.find((w) => w.name === locationName);
      return wh ? { lat: wh.lat ?? 0, lon: wh.lon ?? 0, shippingCostPerMile: 0.90 } : { lat: 0, lon: 0, shippingCostPerMile: 0.90 };
    };

    let newCount = 0;
    let updateCount = 0;

    previewRows.forEach((row) => {
      if (row.status === 'Invalid') return;

      if (row.status === 'Update') {
        // Mutate the existing stockLevel record
        const existing = stockLevels.find((sl) => sl.id === row._existingId);
        if (existing) {
          existing.quantityOnHand = row.quantityOnHand;
          existing.lastUpdated    = today;
        }
        updateCount++;
      } else {
        // New record
        stockLevels.unshift({
          id:               `sl-import-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          partNumber:       row.partNumber,
          locationName:     row.locationName,
          locationType:     locType,
          quantityOnHand:   row.quantityOnHand,
          lastUpdated:      today,
          ...geoFor(row.locationName),
        });
        newCount++;
      }
    });

    onImported(locType, newCount, updateCount);
  };

  const newCount    = previewRows.filter((r) => r.status === 'New').length;
  const updateCount = previewRows.filter((r) => r.status === 'Update').length;
  const invalidCount = previewRows.filter((r) => r.status === 'Invalid').length;
  const importDisabled = newCount + updateCount === 0;

  const toggleSx = {
    textTransform: 'none',
    fontSize: '0.8125rem',
    fontWeight: 500,
    px: 2,
    py: '5px',
    borderRadius: '6px !important',
    border: '1px solid var(--grey-200) !important',
    color: 'var(--grey-600)',
    '&.Mui-selected': {
      backgroundColor: '#E0F2F1',
      color: '#00695C',
      fontWeight: 700,
      borderColor: '#009688 !important',
    },
    '&:hover': { backgroundColor: '#f0faf9' },
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => { if (reason !== 'backdropClick') onClose(); }}
      maxWidth="md"
      fullWidth
    >
      {/* Title */}
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)',
        pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        Import Inventory
        <IconButton size="small" onClick={onClose} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1 }}>
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '12px !important' }}>

        {/* ── Location Type selector (always visible) ── */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--grey-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Location Type <Typography component="span" sx={{ color: '#d32f2f', fontSize: '0.75rem' }}>*</Typography>
          </Typography>
          <ToggleButtonGroup
            exclusive
            value={locType}
            onChange={(_, val) => {
              if (!val) return;
              setLocType(val);
              if (step === 2) { setStep(1); setFileName(''); setPreviewRows([]); }
            }}
            sx={{ gap: '6px', '& .MuiToggleButtonGroup-grouped': { borderRadius: '6px !important', border: '1px solid var(--grey-200) !important', mx: 0 } }}
          >
            {LOCATION_TYPES.map((t) => (
              <ToggleButton key={t} value={t} sx={toggleSx}>{t}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {/* ══════════ STEP 1 — UPLOAD ══════════ */}
        {step === 1 && (
          <>
            <Box
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              sx={{ border: `2px dashed ${dragOver ? '#009688' : 'var(--grey-300)'}`,
                borderRadius: '10px',
                backgroundColor: dragOver ? '#E0F2F1' : '#fafafa',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', gap: 1.5, py: 5, cursor: 'pointer',
                transition: 'all 0.15s',
                '&:hover': { borderColor: '#009688', backgroundColor: '#f0faf9' },
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 40, color: dragOver ? '#009688' : 'var(--grey-400)' }} />
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--grey-700)' }}>
                  Drag & drop your file here
                </Typography>
                <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', mt: '2px' }}>
                  or{' '}
                  <Typography component="span" sx={{ color: '#009688', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}>
                    Browse
                  </Typography>
                  {' '}to choose a file
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-400)' }}>
                Accepted: .xlsx, .xls, .csv
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
              <Typography
                onClick={(e) => e.preventDefault()}
                sx={{ fontSize: '0.8125rem', color: '#009688', cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
              >
                ↓ Download Template
              </Typography>
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-400)', fontWeight: 600, mb: '2px' }}>
                  Expected columns
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)' }}>
                  Part # · Location Name · Qty on Hand
                </Typography>
              </Box>
            </Box>
          </>
        )}

        {/* ══════════ STEP 2 — PREVIEW ══════════ */}
        {step === 2 && (
          <>
            {/* File name row */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <InsertDriveFileOutlinedIcon sx={{ fontSize: 18, color: 'var(--grey-400)' }} />
              <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-700)', fontWeight: 500 }}>{fileName}</Typography>
              <Typography
                onClick={() => setStep(1)}
                sx={{ fontSize: '0.8125rem', color: '#009688', cursor: 'pointer', ml: 1, '&:hover': { textDecoration: 'underline' } }}
              >
                Change file
              </Typography>
            </Box>

            {/* Summary chips */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-600)', fontWeight: 600 }}>
                Import summary:
              </Typography>
              {newCount > 0 && (
                <Box sx={{ display: 'inline-flex', px: '10px', py: '3px', borderRadius: '10px', backgroundColor: '#E0F2F1' }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#00695C' }}>{newCount} new</Typography>
                </Box>
              )}
              {updateCount > 0 && (
                <Box sx={{ display: 'inline-flex', px: '10px', py: '3px', borderRadius: '10px', backgroundColor: '#E3F2FD' }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#1565C0' }}>{updateCount} update{updateCount !== 1 ? 's' : ''}</Typography>
                </Box>
              )}
              {invalidCount > 0 && (
                <Box sx={{ display: 'inline-flex', px: '10px', py: '3px', borderRadius: '10px', backgroundColor: '#FFEBEE' }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#B71C1C' }}>{invalidCount} invalid</Typography>
                </Box>
              )}
            </Box>

            {/* Preview table */}
            <Box sx={{ border: '1px solid var(--grey-200)', borderRadius: '8px', overflow: 'hidden' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Part #', 'Location Name', 'Qty on Hand', 'Status'].map((h) => (
                      <TableCell key={h} sx={TH}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewRows.map((row, idx) => (
                    <TableRow
                      key={idx}
                      sx={{ height: 44,
                        '&:not(:last-child) td': { borderBottom: '1px solid var(--grey-200)' },
                        opacity: row.status === 'Invalid' ? 0.5 : 1,
                      }}
                    >
                      <TableCell sx={{ ...TD, fontWeight: 600, color: row.status === 'New' ? '#009688' : 'var(--grey-700)' }}>
                        {row.partNumber}
                      </TableCell>
                      <TableCell sx={TD}>{row.locationName}</TableCell>
                      <TableCell sx={{ ...TD, fontWeight: 600 }}>{row.quantityOnHand}</TableCell>
                      <TableCell sx={TD}><StatusChip status={row.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)' }}>
              <strong>New</strong> records will be added. <strong>Update</strong> records will refresh the existing qty on hand
              .{invalidCount > 0 && <> <strong>Invalid</strong> part numbers are not in the catalog and will be skipped.</>}
            </Typography>
          </>
        )}
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: 'space-between' }}>
        <Box>
          {step === 2 && (
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => setStep(1)}
              sx={{ textTransform: 'none', color: 'var(--grey-600)', fontWeight: 500, fontSize: '0.875rem', '&:hover': { backgroundColor: 'var(--grey-50)' } }}
            >
              Back
            </Button>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Typography
            onClick={onClose}
            sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', cursor: 'pointer', '&:hover': { color: 'var(--grey-700)' } }}
          >
            Cancel
          </Typography>

          {step === 1 ? (
            <Button
              variant="contained"
              size="small"
              onClick={() => fileInputRef.current?.click()}
              sx={{ backgroundColor: '#009688', '&:hover': { backgroundColor: '#00796B' },
                textTransform: 'none', fontWeight: 600, fontSize: '0.875rem',
                boxShadow: 'none', px: 2,
              }}
            >
              Browse File
            </Button>
          ) : (
            <Button
              variant="contained"
              size="small"
              disabled={importDisabled}
              onClick={handleImport}
              sx={{ backgroundColor: '#009688', '&:hover': { backgroundColor: '#00796B' },
                '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-500)' },
                textTransform: 'none', fontWeight: 600, fontSize: '0.875rem',
                boxShadow: 'none', px: 2,
              }}
            >
              Import {newCount + updateCount} record{(newCount + updateCount) !== 1 ? 's' : ''}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
